import {isPositiveNumber} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {IngredientMeasurementUnit} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isAmountQs} from 'notebook/integrations/registry/MixtureGroupConstants';
import {
  ORDERED_INGREDIENT_UNITS_FOR_DISPLAY,
  ORDERED_MIXTURE_UNITS_FOR_DISPLAY,
} from 'registry/mixtures/MixtureConstants';
import APIUtil from 'util/APIUtil';
import ErrorSubclass from 'util/ErrorSubclass';
import {Optional} from 'util/Types';

/**
 * This frontend validation module adapts a subset of the backend validation functions at
 * `benchling/registry/mixtures/validation.py` for frontend use.
 *
 * Notably, this module does not validate that an inherited ingredient's component, catalog ID, and units
 * cannot be edited. These functions also accept the minimum set of information needed because frontend data
 * varies in shape and this is intended to be widely usable.
 *
 */

export class MixtureValidationError extends ErrorSubclass {}

export function validateIngredientAmount(amount: string | null): void {
  if (amount != null && amount !== '' && !isAmountQs(amount) && !isPositiveNumber(amount)) {
    throw new MixtureValidationError('Ingredient amount must be a positive number or the string "QS"');
  }
}

export function validateIngredientComponentBioentity(
  componentBioentityId: Optional<string>,
  mixtureId: string
): void {
  assert(APIUtil.isMixture(mixtureId), 'Mixture ID should be valid');
  if (componentBioentityId == null) {
    throw new MixtureValidationError('Ingredients must specify a component entity');
  }
  if (componentBioentityId === mixtureId) {
    throw new MixtureValidationError('Ingredients cannot specify their own mixture as the component entity');
  }
}

export function validateIngredientUnits(units: string): void {
  const allowedUnits: Array<string> = Object.values(IngredientMeasurementUnit);
  if (!allowedUnits.includes(units)) {
    throw new MixtureValidationError(
      `Ingredient units must be one of: ${ORDERED_INGREDIENT_UNITS_FOR_DISPLAY.join(', ')}`
    );
  }
}

export function validateMixtureAmount(amount: string | null): void {
  if (amount == null || !isPositiveNumber(amount)) {
    throw new MixtureValidationError(`Mixture amount must be a positive number`);
  }
}

export function validateMixtureUnits(units: string): void {
  const allowedUnits: Array<string> = Object.values(ORDERED_MIXTURE_UNITS_FOR_DISPLAY);
  if (!allowedUnits.includes(units)) {
    throw new MixtureValidationError(`Mixture units must be one of: ${allowedUnits.join(', ')}`);
  }
}

export function validateMixtureComponentBioentitiesAreUnique(
  ingredientComponentBioentityIds: Array<string>
): void {
  const uniqueComponentIds = new Set(ingredientComponentBioentityIds);
  if (uniqueComponentIds.size < ingredientComponentBioentityIds.length) {
    throw new MixtureValidationError('Ingredients within a mixture cannot specify the same component entity');
  }
}

export function validateMixtureAtMostOneQS(ingredientAmounts: Array<string | null>): void {
  if (ingredientAmounts.filter((amt) => amt != null && isAmountQs(amt)).length > 1) {
    throw new MixtureValidationError('At most one ingredient can have amount "QS"');
  }
}
